/**
 * 代理商 API
 */
import request from './api';

// 站点类型
export type SiteType = 'classified' | 'mall' | 'anime' | 'music';

// 站点接口
export interface TenantSite {
  id: string;
  name: string;
  subdomain: string;
  status: 'active' | 'stopped' | 'expired';
  site_type: SiteType;
  platform_disabled?: number;
  domains: string[];
  admin_count: number;
  owner_id?: string;
  owner_name?: string;
  created_at: string;
  expire_at?: string;
  is_default?: boolean;
  province?: string;
  template_id?: string;
}

// 用户接口
export interface TenantUser {
  id: string;
  username: string;
  nickname: string;
  phone: string;
  email: string;
  status: 'active' | 'suspended';
  site_count: number;
  sites?: string;
  created_at: string;
}

// 套餐接口
export interface TenantPlan {
  id: string;
  name: string;
  amount: number;
  site_limit: number;
  benefits: {
    site_limit?: number;
  };
  expire_at?: string;
}

export interface TenantPlanInfo {
  current_plan: TenantPlan | null;
  plans: TenantPlan[];
}

// 仪表盘统计
export interface TenantDashboardStats {
  site_count: number;
  user_count: number;
  listing_count: number;
  vip_expires_at?: string;
}

/**
 * 获取代理商仪表盘
 */
export function getTenantDashboard(): Promise<{ stats: TenantDashboardStats }> {
  return request('/tenant/dashboard');
}

/**
 * 获取站点列表
 */
export function getTenantSites(params?: {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
  province?: string;
  template_id?: string;
}): Promise<{ list: TenantSite[]; total: number; page: number; limit: number }> {
  return request('/tenant/sites', { params });
}

/**
 * 创建站点
 */
export function createTenantSite(data: {
  name: string;
  subdomain: string;
  site_type?: SiteType;
  province?: string;
  expire_at?: string;
  admin_user_id?: string;
}): Promise<{ id: string; name: string; subdomain: string }> {
  return request('/tenant/sites', { method: 'POST', body: JSON.stringify(data) });
}

/**
 * 更新站点
 */
export function updateTenantSite(id: string, data: {
  name?: string;
  site_type?: string;
  status?: string;
  expire_at?: string;
}): Promise<void> {
  return request(`/tenant/sites/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

/**
 * 删除站点
 */
export function deleteTenantSite(id: string): Promise<void> {
  return request(`/tenant/sites/${id}`, { method: 'DELETE' });
}

/**
 * 获取站点详情
 */
export function getTenantSiteDetail(id: string): Promise<TenantSite & { admins: any[] }> {
  return request(`/tenant/sites/${id}`);
}

/**
 * 绑定域名（租户用）
 */
export function bindTenantSiteDomain(siteId: string, domain: string): Promise<{ domains: string[] }> {
  return request(`/tenant/site-domains/${siteId}/bind`, { 
    method: 'POST', 
    body: JSON.stringify({ domain }) 
  });
}

/**
 * 绑定域名（站点管理员用，走admin路由，非租户亦可）
 */
export function bindAdminSiteDomain(siteId: string, domain: string): Promise<{ domains: string[] }> {
  return request(`/admin/site-domains/${siteId}/bind`, { 
    method: 'POST', 
    body: JSON.stringify({ domain }) 
  });
}

/**
 * 解绑域名（租户用）
 */
export function unbindTenantSiteDomain(siteId: string, domain: string): Promise<void> {
  return request(`/tenant/site-domains/${siteId}/unbind`, { 
    method: 'POST', 
    body: JSON.stringify({ domain }) 
  });
}

/**
 * 解绑域名（站点管理员用，走admin路由）
 */
export function unbindAdminSiteDomain(siteId: string, domain: string): Promise<void> {
  return request(`/admin/site-domains/${siteId}/unbind`, { 
    method: 'POST', 
    body: JSON.stringify({ domain }) 
  });
}

/**
 * 获取子用户列表
 */
export function getTenantUsers(params?: {
  page?: number;
  limit?: number;
  search?: string;
}): Promise<{ list: TenantUser[]; total: number; page: number; limit: number }> {
  return request('/tenant/users', { params });
}

/**
 * 创建子用户
 */
export function createTenantUser(data: {
  username: string;
  password: string;
  nickname?: string;
  phone?: string;
  email?: string;
  site_ids?: string[];
}): Promise<{ id: string; username: string }> {
  return request('/tenant/users', { method: 'POST', body: JSON.stringify(data) });
}

/**
 * 更新子用户
 */
export function updateTenantUser(id: string, data: {
  nickname?: string;
  phone?: string;
  email?: string;
  status?: string;
  password?: string;
  site_ids?: string[];
}): Promise<void> {
  return request(`/tenant/users/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

/**
 * 删除子用户
 */
export function deleteTenantUser(id: string): Promise<void> {
  return request(`/tenant/users/${id}`, { method: 'DELETE' });
}

/**
 * 获取子用户详情
 */
export function getTenantUserDetail(id: string): Promise<TenantUser & { sites: { id: string; name: string }[] }> {
  return request(`/tenant/users/${id}`);
}

/**
 * 获取可分配的站点列表
 */
export function getTenantAssignableSites(): Promise<{ id: string; name: string; subdomain: string }[]> {
  return request('/tenant/users/sites');
}

/**
 * 获取套餐信息
 */
export function getTenantPlans(): Promise<TenantPlanInfo> {
  return request('/tenant/plans');
}

/**
 * 购买套餐
 */
export function buyTenantPlan(planId: string): Promise<{ order_id: string; amount: number; plan_name: string }> {
  return request('/tenant/plans/buy', { method: 'POST', body: JSON.stringify({ plan_id: planId }) });
}

// 站点复制进度
export interface CopyStep {
  name: string;
  status: 'pending' | 'running' | 'done' | 'failed';
  count?: number;
}

export interface CopyProgress {
  total: number;
  current: number;
  steps: CopyStep[];
  status: 'idle' | 'copying' | 'done' | 'failed';
  error?: string;
}

export interface CopySiteResult {
  id: string;
  name: string;
  subdomain: string;
  stats: {
    settings: number;
    categories: number;
    single_pages: number;
    nav_menus: number;
    register_fields: number;
    sms_configs: number;
    vip_plans: number;
    listings: number;
  };
  steps: CopyStep[];
}

/**
 * 一键复制站点
 */
export function copyTenantSite(data: {
  source_site_id: string;
  name: string;
  subdomain: string;
  admin_user_id?: string;
  expire_at?: string;
}): Promise<CopySiteResult> {
  return request('/tenant/sites/copy', { method: 'POST', body: JSON.stringify(data) });
}

/**
 * 查询复制进度
 */
export function getSiteCopyProgress(siteId: string): Promise<CopyProgress> {
  return request(`/tenant/sites/${siteId}/copy-progress`);
}
